/**
 * Low Stock Alerts Section
 * @see Sprint 28 - US-28.4
 */

import { useNavigate } from 'react-router-dom';
import { CheckCircle, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Badge, Skeleton, EmptyState, Button } from '@/components/ui';
import { usePermission } from '@/hooks';
import { formatQuantity } from '@/lib/format';
import { useLowStockAlerts } from '../hooks';

export function LowStockAlerts() {
  const navigate = useNavigate();
  const canCreateOrder = usePermission('purchase-orders.create');
  const { data, isLoading, isError } = useLowStockAlerts();

  if (isError) return null;

  const items = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Alertes stock bas</CardTitle>
            <CardDescription>Produits necessitant un reapprovisionnement</CardDescription>
          </div>
          {total > 0 && (
            <Badge variant="danger">{total} alertes</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={CheckCircle}
            title="Aucune alerte de stock"
            description="Tous vos produits sont au-dessus du seuil minimum."
            size="sm"
          />
        ) : (
          <div className="space-y-2">
            {/* Header row */}
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-medium text-neutral-500 px-2 pb-1 border-b border-neutral-100">
              <div className="col-span-4">Produit</div>
              <div className="col-span-2 text-right">Stock</div>
              <div className="col-span-2 text-right">Min.</div>
              <div className="col-span-2 text-center">Urgence</div>
              {canCreateOrder && <div className="col-span-2 text-center">Action</div>}
            </div>
            {/* Data rows */}
            {items.map((item) => (
              <div
                key={item.rawProductId}
                className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 items-center px-2 py-2 rounded-lg hover:bg-neutral-50"
              >
                <div className="sm:col-span-4 font-medium text-sm text-dark-500 truncate">
                  {item.rawProduct.name}
                </div>
                <div className="sm:col-span-2 text-sm text-right text-dark-500">
                  {formatQuantity(item.currentStock)} {item.rawProduct.recipeUnit.symbol}
                </div>
                <div className="sm:col-span-2 text-sm text-right text-neutral-500">
                  {formatQuantity(item.rawProduct.minStock)} {item.rawProduct.recipeUnit.symbol}
                </div>
                <div className="sm:col-span-2 text-center">
                  <Badge variant={item.stockStatus === 'critical' ? 'danger' : 'warning'}>
                    {item.stockStatus === 'critical' ? 'Critique' : 'Bas'}
                  </Badge>
                </div>
                {canCreateOrder && (
                  <div className="sm:col-span-2 text-center">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => navigate('/achats/commandes/create')}
                    >
                      Commander
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {total > 0 && (
        <CardFooter>
          <button
            className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            onClick={() => navigate('/stock/actuel?lowStockOnly=true')}
          >
            Voir tous les produits en alerte
            <ChevronRight className="h-4 w-4" />
          </button>
        </CardFooter>
      )}
    </Card>
  );
}
